import { useState, useCallback, useEffect } from "react"
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function CollapsibleRenderer({ element }: RendererProps) {
  const { dispatchEvent, resolveSymbol } = useRenderContext()

  // External open state via options.open binding
  const openBinding = (element.options as Record<string, unknown> | undefined)?.open
  const resolvedOpen =
    openBinding != null ? resolveSymbol(String(openBinding)) : undefined

  const defaultOpen =
    (element as unknown as Record<string, unknown>).defaultOpen === true ||
    element.variant === "open"

  const [isOpen, setIsOpen] = useState(() => {
    if (resolvedOpen !== undefined) return Boolean(resolvedOpen)
    return defaultOpen
  })

  useEffect(() => {
    if (resolvedOpen !== undefined) {
      setIsOpen(Boolean(resolvedOpen))
    }
  }, [resolvedOpen])

  const children = element.children ?? []
  const trigger = children[0]
  const content = children.slice(1)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      dispatchEvent(element.id, "tap", { open })
    },
    [element.id, dispatchEvent],
  )

  return (
    <CollapsiblePrimitive.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={cn(element.className)}
      data-testid={`collapsible-${element.id}`}
    >
      {trigger && (
        <CollapsiblePrimitive.Trigger asChild>
          <div className="cursor-pointer">
            <ElementRenderer element={trigger} />
          </div>
        </CollapsiblePrimitive.Trigger>
      )}
      <CollapsiblePrimitive.Content className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        {content.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  )
}
